import { AntDesign } from "@expo/vector-icons";
import {
  ProductThumbnail,
  Stack,
  Text,
} from "@towersystems/roam-common-native";
import { useThemeProvider } from "@towersystems/roam-common-native/lib/utilities/theme";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import { TouchableOpacity } from "react-native";
import { Spacings, View } from "react-native-ui-lib";
import { moneyToFormattedString } from "../../../../utilities/money";
import { lowestPrice } from "../../../../utilities/product";
import { toTitleCase } from "../../../../utilities/to-title-case";
import ProductModel from "../../../../utilities/data/model/Product";

export type ProductListItemProps<T extends Product | ProductModel> = {
  product: T;
  variants?: T[];
  action?: () => void;
  actionLabel?: string;
  paddingVertical?: number;
};

export function ProductListItem<T extends Product | ProductModel>({
  product,
  variants = [],
  action,
  actionLabel,
  paddingVertical,
}: ProductListItemProps<T>) {
  const { colors } = useThemeProvider();

  function getImageUrl(product: T) {
    if (product instanceof ProductModel) {
      return product.imageUrl;
    } else {
      return product.image?.path;
    }
  }

  const image = getImageUrl(product);

  return (
    <TouchableOpacity
      onPress={action}
      style={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        paddingVertical: paddingVertical,
      }}
    >
      <View row centerV style={{ flexBasis: "100%", flexShrink: 1 }}>
        <View style={{ marginRight: Spacings.s4 }}>
          <ProductThumbnail size="large" imageUrl={image} />
        </View>
        <View style={{ flexShrink: 1 }}>
          <Stack spacing={Spacings.s2} vertical>
            <View>
              <Text
                variant="heading"
                fontWeight="medium"
                numberOfLines={1}
                ellipsizeMode="head"
              >
                {toTitleCase(product.name)}
              </Text>
              {product.displayName ? (
                <Text color="subdued" variant="heading5">
                  {product.displayName}
                </Text>
              ) : undefined}
            </View>
            {product.hasVariants ? (
              <View>
                <Text
                  color="subdued"
                  variant="heading5"
                >{`${product.totalVariants} Variants`}</Text>
              </View>
            ) : (
              <View>
                <Text color="subdued" variant="heading5">
                  {product.stockNumber}
                </Text>
              </View>
            )}
          </Stack>
        </View>
      </View>
      <View style={{ paddingLeft: Spacings.s6 }} row centerV>
        <View>
          {actionLabel ? (
            <Text variant="heading1">{actionLabel}</Text>
          ) : (
            <View style={{ alignItems: "flex-end" }}>
              {product.hasVariants && (
                <Text color="subdued" variant="heading5">
                  FROM
                </Text>
              )}
              <Text variant="heading1">
                {moneyToFormattedString(lowestPrice(product, variants))}
              </Text>
            </View>
          )}
        </View>

        <AntDesign
          style={[{ marginLeft: Spacings.s4 }]}
          name="right"
          size={24}
          color={colors.text}
        />
      </View>
    </TouchableOpacity>
  );
}
